/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import {
	AddressFormattingSettings,
	NameFormattingSettings,
} from '@models/businessSettings/settings';
import { Business, BusinessAddress, BusinessUser } from '@models/businesses';
import {
	BusinessMatterClient,
	BusinessMatterClientWithAssociations,
	CalculatedLandTransferTax,
	ClientsCapacity,
	Matter,
	MatterFileType,
	MatterTitle,
	PersonMatterClient,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { MatterFormData, PDFFormType } from '@models/matter-form-data';
import {
	MatterMortgagePriority,
	MatterMortgageWithAssociations,
} from '@models/mortgage';
import {
	Action,
	Selector,
	State,
	StateContext,
	createSelector,
} from '@ngxs/store';
import { BusinessState } from '@state/business';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { LTTSelector } from '@state/global/ltt.selectors';
import { MatterClients } from '@state/global/matter_clients';
import { BaseFormMapper } from 'src/app/private/sale/documents/forms/mappers/base-form-mapper';
import {
	AdjustmentsSelector,
	AllAdjustmentsCalculated,
} from '../adjustments/adjustments.selectors';
import { SelectedMatter } from '../matter.selectors';
import { MatterMortgagesState } from '../matter_mortgages';
import { MatterTitlesState } from '../titles/titles.state';
import * as actions from './form-data.actions';
import {
	FirestoreStateBaseForMatterWithSubscription,
	MatterWithSubscriptionStateModel,
} from '@state/FirestoreStateBaseForMatter';
import { CollectionPaths } from '@models/collections';
import { Jurisdiction } from '@models/jurisdictions';
import { MatterJurisdictionSelectors } from '../jurisdictions/jurisdictions.selectors';
import { DEFAULT_STATE_BASE } from '@state/FirestoreStateBase';

export interface MatterFormDataStateModel
	extends MatterWithSubscriptionStateModel {
	items: MatterFormData[];
}
type Ctx = StateContext<MatterFormDataStateModel>;

export interface MergedFormData {
	mappings: any;
	formData: any;
	finalMergedData: any;
}

const DEFAULT_STATE: MatterFormDataStateModel = {
	...DEFAULT_STATE_BASE,
	items: [],
};

@State<MatterFormDataStateModel>({
	name: 'matterFormData',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class MatterFormDataState extends FirestoreStateBaseForMatterWithSubscription<
	MatterFormData,
	MatterFormDataStateModel
> {
	constructor() {
		super(
			(pathParams) =>
				CollectionPaths.business_matter_formData(
					pathParams.businessId,
					pathParams.matterId,
				),
			(payload) => ({ items: payload as MatterFormData[] }),
			() => DEFAULT_STATE,
		);
	}

	@Selector([MatterFormDataState])
	static getAllFormData(state: MatterFormDataStateModel): MatterFormData[] {
		return state?.items;
	}

	@Selector()
	static getFormData(state: MatterFormDataStateModel) {
		return (id: string): MatterFormData =>
			state.items.find((item) => item.id === id);
	}

	static getMergedFormData(
		pdfFormType: PDFFormType,
		formMapper: BaseFormMapper,
		formId?: string,
		mortgageId?: string,
	) {
		const selector = createSelector(
			[
				MatterFormDataState.getAllFormData,
				SelectedMatter.getSelected,
				BusinessState.getNameFormattingSettings,
				MatterClients.get_sellers,
				MatterClients.get_buyers,
				AdjustmentsSelector.allAdjustmentsCalculated,
				BusinessState.getBusiness,
				MatterTitlesState.getAll,
				MatterClients.get_matterClientsWithAssociationMapped,
				LTTSelector.lttCalculated,
				SelectedMatter.office,
				MatterClients.get_otherParties,
				MatterClients.get_clientsCapacity,
				MatterMortgagesState.getAllMortgagesWithAssociations,
				BusinessUsersState.getUsers,
				MatterJurisdictionSelectors.getPrimary,
				MatterClients.get_signorsWithAssociationMapped,
				MatterClients.get_otherPartyWithAssociationMapped,
				SelectedMatter.matterFileType,
				BusinessState.getAddressFormattingSettings,
			],
			(
				formDatas: MatterFormData[],
				matter: Matter,
				nameFormattingSettings: NameFormattingSettings,
				sellers: (PersonMatterClient | BusinessMatterClient)[],
				buyers: (PersonMatterClient | BusinessMatterClient)[],
				adjustmentsCalculated: AllAdjustmentsCalculated,
				business: Business,
				titles: MatterTitle[],
				matterClients: (
					| PersonMatterClientWithAssociations
					| BusinessMatterClientWithAssociations
				)[],
				lttCalculated: CalculatedLandTransferTax,
				businessAddress: BusinessAddress,
				otherParties: PersonMatterClient[],
				clientsCapacity: ClientsCapacity,
				mortgages: MatterMortgageWithAssociations[],
				businessUsers: BusinessUser[],
				primaryJurisdiction: Jurisdiction,
				signors: PersonMatterClientWithAssociations[],
				otherPartiesWithAssociations: (
					| PersonMatterClientWithAssociations
					| BusinessMatterClientWithAssociations
				)[],
				matterFileType: MatterFileType,
				addressFormattingSettings: AddressFormattingSettings,
			) => {
				const formData = formDatas.find(
					(formData) => formData.id === (formId || pdfFormType),
				);

				const data = {};
				const mergedFormData = formMapper.mergeFormData(
					formData?.values,
					matter,
					nameFormattingSettings,
					sellers,
					buyers,
					adjustmentsCalculated,
					business,
					titles,
					matterClients,
					lttCalculated,
					businessAddress,
					otherParties,
					clientsCapacity,
					mortgages.find((mortgage) =>
						mortgageId
							? mortgage?.id === mortgageId
							: mortgage?.priority === MatterMortgagePriority.PRIORITY_1,
					),
					businessUsers,
					primaryJurisdiction,
					signors,
					otherPartiesWithAssociations,
					matterFileType,
					addressFormattingSettings,
				);

				const mappings = formMapper.CloserToFormMappings;
				if (mergedFormData) {
					Object.entries(mergedFormData).forEach(([key, value]) => {
						data[mappings[key]] = value;
					});
				}

				return {
					mappings,
					formData: mergedFormData,
					finalMergedData: data,
				} as MergedFormData;
			},
		);

		return selector;
	}

	@Action(actions.SetField)
	async setField(ctx: Ctx, { payload }: actions.SetField): Promise<void> {
		console.log('SetField', payload);
		await this.repo.CreateOrMerge(payload.id, {
			name: payload.name,
			type: payload.type,
			url: payload.url,
			values: { [payload.field]: payload.value },
		});
	}
}
